import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@surface/components';

import { CHUNK_LOAD_RECOVERY_COUNTDOWN_SECONDS } from './chunk-load-constants';
import { markChunkLoadRecoveryAttempt } from './chunk-load-recovery-session';
import { reloadPage } from './reload-page';

export interface ChunkLoadRecoveryProps {
  error: Error;
  eventId?: string;
  countdownSeconds?: number;
  /**
   * When false, the countdown stays on its first second. Stories use this so
   * the canvas does not reload the preview.
   */
  autoStart?: boolean;
  reload?: () => void;
}

const reloadButtonLabel = (seconds: number): string => {
  const unit = seconds === 1 ? 'second' : 'seconds';
  return `Reload (in ${seconds} ${unit})`;
};

/**
 * Same error card as {@link ErrorReport}, with the feedback button replaced by
 * a countdown. The button reloads immediately when clicked, and on its own
 * when the countdown reaches zero.
 */
export const ChunkLoadRecovery = ({
  error,
  eventId,
  countdownSeconds = CHUNK_LOAD_RECOVERY_COUNTDOWN_SECONDS,
  autoStart = true,
  reload = reloadPage,
}: ChunkLoadRecoveryProps) => {
  const [secondsLeft, setSecondsLeft] = useState(countdownSeconds);
  const hasReloadedRef = useRef(false);

  const reloadNow = useCallback(() => {
    if (hasReloadedRef.current) {
      return;
    }
    if (!markChunkLoadRecoveryAttempt()) {
      return;
    }
    hasReloadedRef.current = true;
    reload();
  }, [reload]);

  useEffect(() => {
    if (!autoStart) {
      return;
    }

    let remaining = countdownSeconds;
    const intervalId = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        window.clearInterval(intervalId);
        reloadNow();
        return;
      }
      setSecondsLeft(remaining);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [autoStart, countdownSeconds, reloadNow]);

  return (
    <div
      data-testid="chunk-load-recovery"
      className="min-h-[400px] p-8 flex flex-col items-center justify-center text-center"
    >
      <div className="max-w-md space-y-6">
        <div className="bg-white p-6 rounded-xl border border-gray-50 shadow-md">
          <h2
            data-testid="chunk-load-recovery-title"
            className="text-xl font-semibold mb-2"
          >
            Something went wrong 😞
          </h2>
          <p
            data-testid="chunk-load-recovery-message"
            className="text-gray-600 mb-4"
          >
            Looks like this unit needs some repairs! Our team has been notified.
            If you would like to add notes to the work order we generate please
            use the button below.
          </p>
          <div className="space-y-2 text-left bg-white p-4 rounded-lg border border-gray-200">
            <p className="text-sm font-medium">Maintenance Report:</p>
            <p
              data-testid="chunk-load-recovery-details"
              className="text-sm text-gray-600 font-mono break-all"
            >
              {error.message}
            </p>
          </div>
        </div>

        <Button
          data-testid="chunk-load-recovery-countdown"
          aria-live="polite"
          onClick={reloadNow}
        >
          {reloadButtonLabel(secondsLeft)}
        </Button>

        {eventId && (
          <p
            data-testid="chunk-load-recovery-reference"
            className="text-sm text-gray-500"
          >
            Reference #: {eventId.slice(0, 8)}
          </p>
        )}
      </div>
    </div>
  );
};
